import { useState } from 'react';
import { useQuery } from 'convex/react';
import { adminSession, type PaymentsList } from '@/lib/api';
import { friendlyError } from '@/lib/errors';
import { PaymentsTable } from '@/components/PaymentsTable';
import { api } from '../../../convex/functions/_generated/api';

/**
 * /dashboard/payments — global payments + refunds ledger.
 *
 * Paginated table, 50 rows per page, read-only (refunds happen in
 * the store consoles — see PaymentsTable). The shared PaymentsTable
 * handles the row render; this page is just the data load +
 * pagination shell.
 */
export function PaymentsPage() {
  const [page, setPage] = useState(1);
  const pageSize = 50;
  const token = adminSession.getToken();

  // useQuery is reactive — backend ConvexError surfaces by throwing
  // synchronously during render. We catch it to render the inline
  // error banner instead of unmounting through an ErrorBoundary.
  let data: PaymentsList | undefined;
  let err: string | null = null;
  try {
    data = useQuery(
      api.admin.payments.listPaymentsQuery,
      token ? { token, page, pageSize } : 'skip',
    ) as PaymentsList | undefined;
  } catch (e) {
    err = friendlyError(e, 'listPayments');
  }

  const loading = data === undefined && !err;
  const totalPages = data ? Math.max(1, Math.ceil(data.total / pageSize)) : 1;

  return (
    <div>
      <header style={{ marginBottom: 20 }}>
        <h2 className="serif" style={{ fontSize: 30, fontWeight: 500, fontStyle: 'italic', margin: 0 }}>
          Payments
        </h2>
        <div style={{ color: 'var(--ink-3)', fontSize: 13, marginTop: 4 }}>
          {data ? `${data.total.toLocaleString()} charges on record.` : 'Loading…'}
        </div>
      </header>

      {err && (
        <div style={{ background: 'rgba(208,64,48,0.08)', color: '#a8331f', padding: '10px 14px', borderRadius: 12, fontSize: 13, marginBottom: 16 }}>
          {err}
        </div>
      )}

      {loading ? (
        <div style={{ color: 'var(--ink-3)', padding: 32, textAlign: 'center' }}>Loading…</div>
      ) : !data ? null : (
        <>
          <PaymentsTable payments={data.payments} showUser />

          {totalPages > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginTop: 16, flexWrap: 'wrap' }}>
              <div className="mono" style={{ fontSize: 11, color: 'var(--ink-3)', letterSpacing: 1 }}>
                Page {data.page} / {totalPages}
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1 || loading}
                  style={paginationBtn(page <= 1)}
                >
                  ← Newer
                </button>
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages || loading}
                  style={paginationBtn(page >= totalPages)}
                >
                  Older →
                </button>
              </div>
            </div>
          )}
        </>
      )}

      <ConsumableGrantsPanel token={token} />
    </div>
  );
}

/**
 * Consumable IAP grants — one-time "premium action" purchases (DM passes,
 * boosts, keeper spots, …). Read-only visibility so admins can confirm
 * purchases are landing + spot refunds. See docs/iap-consumables-runbook.md.
 */
function ConsumableGrantsPanel({ token }: { token: string | null }) {
  let grants: ConsumableGrantRow[] | undefined;
  let err: string | null = null;
  try {
    const data = useQuery(
      api.admin.payments.listConsumableGrantsQuery,
      token ? { token, limit: 100 } : 'skip',
    ) as { grants: ConsumableGrantRow[]; count: number } | undefined;
    grants = data?.grants;
  } catch (e) {
    err = friendlyError(e, 'listConsumableGrants');
  }

  return (
    <section style={{ marginTop: 40 }}>
      <h3 className="serif" style={{ fontSize: 22, fontWeight: 500, fontStyle: 'italic', margin: '0 0 4px' }}>
        Consumable purchases
      </h3>
      <div style={{ color: 'var(--ink-3)', fontSize: 13, marginBottom: 14 }}>
        One-time premium actions (passes, boosts, spots). Newest 100.
      </div>

      {err && (
        <div style={{ background: 'rgba(208,64,48,0.08)', color: '#a8331f', padding: '10px 14px', borderRadius: 12, fontSize: 13, marginBottom: 16 }}>
          {err}
        </div>
      )}

      {grants === undefined && !err ? (
        <div style={{ color: 'var(--ink-3)', padding: 24, textAlign: 'center' }}>Loading…</div>
      ) : grants && grants.length === 0 ? (
        <div style={{ color: 'var(--ink-3)', padding: 24, textAlign: 'center', fontSize: 13 }}>
          No consumable purchases yet.
        </div>
      ) : grants ? (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: 'left', color: 'var(--ink-3)' }}>
                <th style={th}>User</th>
                <th style={th}>Action</th>
                <th style={th}>Price</th>
                <th style={th}>Status</th>
                <th style={th}>When</th>
              </tr>
            </thead>
            <tbody>
              {grants.map((g) => (
                <tr key={g.id} style={{ borderTop: '1px solid var(--line)' }}>
                  <td style={td}>{g.display_name}</td>
                  <td style={td}>
                    <span className="mono" style={{ fontSize: 12 }}>{g.action}</span>
                    {g.is_sandbox && (
                      <span style={sandboxPill}>sandbox</span>
                    )}
                  </td>
                  <td style={td}>
                    {g.price_usd != null ? `$${g.price_usd.toFixed(2)}` : '—'}
                  </td>
                  <td style={td}>
                    {g.revoked ? (
                      <span style={{ color: '#a8331f' }}>revoked</span>
                    ) : g.refunded ? (
                      <span style={{ color: '#a8331f' }}>refunded</span>
                    ) : g.active_until && g.active_until > Date.now() ? (
                      <span style={{ color: 'var(--coral, #d04030)' }}>active</span>
                    ) : g.bound_target_type ? (
                      <span style={{ color: 'var(--ink-3)' }}>bound</span>
                    ) : (
                      <span style={{ color: 'var(--ink-2)' }}>{g.qty_remaining}/{g.qty_total} left</span>
                    )}
                  </td>
                  <td style={{ ...td, color: 'var(--ink-3)', whiteSpace: 'nowrap' }}>
                    {new Date(g.purchased_at).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </section>
  );
}

interface ConsumableGrantRow {
  id: string;
  display_name: string;
  action: string;
  product_id: string;
  price_usd: number | null;
  is_sandbox: boolean;
  qty_remaining: number;
  qty_total: number;
  bound_target_type: string | null;
  active_until: number | null;
  refunded: boolean;
  revoked: boolean;
  purchased_at: number;
}

const th: React.CSSProperties = { padding: '8px 10px', fontSize: 11, letterSpacing: 1, textTransform: 'uppercase', fontWeight: 600 };
const td: React.CSSProperties = { padding: '10px', color: 'var(--ink)' };
const sandboxPill: React.CSSProperties = {
  marginLeft: 8,
  fontSize: 10,
  padding: '1px 6px',
  borderRadius: 100,
  background: 'var(--chip)',
  color: 'var(--ink-3)',
  textTransform: 'uppercase',
  letterSpacing: 1,
};

function paginationBtn(disabled: boolean): React.CSSProperties {
  return {
    background: disabled ? 'var(--chip)' : 'var(--surface)',
    color: disabled ? 'var(--ink-3)' : 'var(--ink)',
    border: '1px solid var(--line-strong)',
    borderRadius: 100,
    padding: '6px 12px',
    fontSize: 12,
    fontWeight: 600,
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontFamily: 'var(--font-ui)',
    opacity: disabled ? 0.5 : 1,
  };
}
